import React, { useState, useMemo } from 'react'
import { IoChevronDown, IoChevronUp, IoClose } from 'react-icons/io5'
import { LEAGUES, LEAGUES_BY_COUNTRY, COUNTRY_FLAGS } from '../context/LeagueContext'
import { useUpcomingFixtures } from '../swr-config'

export type ValueBetsFilters = {
  minOdds: number | null
  maxOdds: number | null
  minEdge: number | null
  maxEdge: number | null
  marketCategories: string[]
  leagueIds: number[]
  fixtureId: number | null
  sortBy: 'edge' | 'odds' | 'kickoff' | 'conviction' | 'xg'
  sortOrder: 'asc' | 'desc'
  daysAhead: number
}

const MARKET_CATEGORIES = [
  { key: 'match_result', label: 'Match Result' },
  { key: 'goals', label: 'Goals' },
  { key: 'corners', label: 'Corners' },
  { key: 'fouls_cards', label: 'Cards' },
  { key: 'shots', label: 'Shots' },
  { key: 'goalscorer', label: 'Goalscorer' },
]

const SORT_OPTIONS = [
  { value: 'edge', label: 'Highest Edge' },
  { value: 'odds', label: 'Best Odds' },
  { value: 'conviction', label: 'Highest Conviction' },
  { value: 'xg', label: 'Highest xG' },
  { value: 'kickoff', label: 'Kickoff Time' },
]

const DATE_PRESETS = [
  { value: 1, label: 'Today' },
  { value: 2, label: '2 Days' },
  { value: 3, label: '3 Days' },
  { value: 7, label: '7 Days' },
  { value: 14, label: '14 Days' },
]

interface ValueBetsFilterBarProps {
  filters: ValueBetsFilters
  onChange: (filters: ValueBetsFilters) => void
}

export function ValueBetsFilterBar({ filters, onChange }: ValueBetsFilterBarProps) {
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [fixtureSearch, setFixtureSearch] = useState('')

  // Fetch upcoming fixtures for the dropdown
  const { data: fixturesData } = useUpcomingFixtures(7)

  // Filter fixtures based on search
  const filteredFixtures = useMemo(() => {
    if (!fixturesData) return []
    const search = fixtureSearch.toLowerCase()
    return fixturesData.filter(
      (f) =>
        f.home_team.name.toLowerCase().includes(search) ||
        f.away_team.name.toLowerCase().includes(search)
    )
  }, [fixturesData, fixtureSearch])

  // Get selected fixture label
  const selectedFixture = useMemo(() => {
    if (!filters.fixtureId || !fixturesData) return null
    return fixturesData.find((f) => f.id === filters.fixtureId)
  }, [filters.fixtureId, fixturesData])

  const toggleCategory = (key: string) => {
    const current = filters.marketCategories
    const updated = current.includes(key)
      ? current.filter((c) => c !== key)
      : [...current, key]
    onChange({ ...filters, marketCategories: updated })
  }

  const toggleLeague = (leagueId: number) => {
    const current = filters.leagueIds
    const updated = current.includes(leagueId)
      ? current.filter((id) => id !== leagueId)
      : [...current, leagueId]
    onChange({ ...filters, leagueIds: updated })
  }

  const advancedCount =
    filters.marketCategories.length +
    filters.leagueIds.length +
    (filters.fixtureId != null ? 1 : 0) +
    (filters.minEdge != null && filters.minEdge !== 0 ? 1 : 0) +
    (filters.maxEdge != null ? 1 : 0) +
    (filters.minOdds != null ? 1 : 0) +
    (filters.maxOdds != null ? 1 : 0)

  return (
    <div className="value-bets-filter-bar">
      {/* Date presets - always visible */}
      <div className="filter-row filter-row-dates">
        <label>Time range:</label>
        <div className="filter-pills">
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.value}
              className={`pill pill-clickable ${
                filters.daysAhead === preset.value ? 'pill-active' : ''
              }`}
              onClick={() => onChange({ ...filters, daysAhead: preset.value })}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Sort dropdown and advanced toggle */}
      <div className="filter-row filter-row-main">
        <div className="filter-row-left">
          <label>Sort by:</label>
          <select
            value={filters.sortBy}
            onChange={(e) =>
              onChange({ ...filters, sortBy: e.target.value as ValueBetsFilters['sortBy'] })
            }
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            className={`sort-order-btn ${filters.sortOrder === 'desc' ? 'active' : ''}`}
            onClick={() =>
              onChange({
                ...filters,
                sortOrder: filters.sortOrder === 'desc' ? 'asc' : 'desc',
              })
            }
          >
            {filters.sortOrder === 'desc' ? '↓' : '↑'}
          </button>
        </div>

        {/* Advanced filters toggle */}
        <button
          className="advanced-toggle"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? (
            <>
              Hide Advanced <IoChevronUp size={14} />
            </>
          ) : (
            <>
              Show Advanced {advancedCount > 0 && <span className="filter-count">({advancedCount})</span>} <IoChevronDown size={14} />
            </>
          )}
        </button>
      </div>

      {showAdvanced && (
        <div className="advanced-filters">
          {/* Category pills */}
          <div className="filter-row">
            <label>Markets:</label>
            <div className="filter-pills">
              {MARKET_CATEGORIES.map((cat) => (
                <button
                  key={cat.key}
                  className={`pill pill-clickable ${
                    filters.marketCategories.includes(cat.key) ? 'pill-active' : ''
                  }`}
                  onClick={() => toggleCategory(cat.key)}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* League pills - grouped by country */}
          <div className="filter-row filter-row-leagues">
            <label>Leagues:</label>
            <div className="filter-pills-grouped">
              {Object.entries(LEAGUES_BY_COUNTRY).map(([country, leagues]) => (
                <div key={country} className="filter-pills-group">
                  <span className="filter-group-label">{COUNTRY_FLAGS[country]}</span>
                  {leagues.map((league) => (
                    <button
                      key={league.api_league_id}
                      className={`pill pill-clickable pill-sm ${
                        filters.leagueIds.includes(league.api_league_id) ? 'pill-active' : ''
                      } ${league.type === 'cup' ? 'pill-cup' : ''}`}
                      onClick={() => toggleLeague(league.api_league_id)}
                      title={league.name}
                    >
                      {league.shortName}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Fixture filter */}
          <div className="filter-row filter-row-fixture">
            <label>Fixture:</label>
            <div className="fixture-filter">
              {selectedFixture ? (
                <div className="fixture-selected">
                  <span>{selectedFixture.home_team.name} vs {selectedFixture.away_team.name}</span>
                  <button
                    className="fixture-clear-btn"
                    onClick={() => onChange({ ...filters, fixtureId: null })}
                    title="Clear fixture filter"
                  >
                    <IoClose size={14} />
                  </button>
                </div>
              ) : (
                <div className="fixture-search-container">
                  <input
                    type="text"
                    placeholder="Search fixture..."
                    value={fixtureSearch}
                    onChange={(e) => setFixtureSearch(e.target.value)}
                    className="fixture-search-input"
                  />
                  {fixtureSearch && filteredFixtures.length > 0 && (
                    <div className="fixture-dropdown">
                      {filteredFixtures.slice(0, 10).map((fixture) => (
                        <button
                          key={fixture.id}
                          className="fixture-option"
                          onClick={() => {
                            onChange({ ...filters, fixtureId: fixture.id })
                            setFixtureSearch('')
                          }}
                        >
                          {fixture.home_team.name} vs {fixture.away_team.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Numeric filters */}
          <div className="filter-row filter-row-numeric">
            <div className="filter-group">
              <label>Min Edge %:</label>
              <input
                type="number"
                value={filters.minEdge ?? ''}
                onChange={(e) =>
                  onChange({
                    ...filters,
                    minEdge: e.target.value ? parseFloat(e.target.value) : null,
                  })
                }
                placeholder="0"
                step="0.5"
              />
            </div>
            <div className="filter-group">
              <label>Max Edge %:</label>
              <input
                type="number"
                value={filters.maxEdge ?? ''}
                onChange={(e) =>
                  onChange({
                    ...filters,
                    maxEdge: e.target.value ? parseFloat(e.target.value) : null,
                  })
                }
                placeholder="No limit"
                step="0.5"
              />
            </div>
            <div className="filter-group">
              <label>Min Odds:</label>
              <input
                type="number"
                value={filters.minOdds ?? ''}
                onChange={(e) =>
                  onChange({
                    ...filters,
                    minOdds: e.target.value ? parseFloat(e.target.value) : null,
                  })
                }
                placeholder="1.01"
                step="0.1"
              />
            </div>
            <div className="filter-group">
              <label>Max Odds:</label>
              <input
                type="number"
                value={filters.maxOdds ?? ''}
                onChange={(e) =>
                  onChange({
                    ...filters,
                    maxOdds: e.target.value ? parseFloat(e.target.value) : null,
                  })
                }
                placeholder="No limit"
                step="0.5"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
